"use client"

import * as React from "react"
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  Trash2,
  CheckCircle2,
  CalendarDays,
  Sparkles,
  MapPin,
  Bell
} from "lucide-react"

interface CalendarAppProps {
  calendarDate?: Date
  setCalendarDate?: (date: Date | undefined) => void
  isWidget?: boolean
}

interface CalendarEvent {
  id: string
  dateStr: string // YYYY-MM-DD
  title: string
  time?: string
  completed?: boolean
}

const BRAZILIAN_HOLIDAYS: Record<string, string> = {
  "01-01": "Ano Novo",
  "04-21": "Tiradentes",
  "05-01": "Dia do Trabalhador",
  "09-07": "Independência do Brasil",
  "10-12": "Nossa Sra. Aparecida",
  "11-02": "Finados",
  "11-15": "Proclamação da República",
  "11-20": "Consciência Negra",
  "12-25": "Natal",
}

const MONTH_NAMES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
]

const WEEK_DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"]
const WEEK_DAYS_FULL = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
]

export function CalendarApp({
  calendarDate: propDate,
  setCalendarDate: propSetDate,
  isWidget = false,
}: CalendarAppProps) {
  const [selectedDate, setSelectedDate] = React.useState<Date>(() => propDate || new Date())
  const [viewDate, setViewDate] = React.useState<Date>(() => propDate || new Date())
  const [newEventTitle, setNewEventTitle] = React.useState("")
  const [newEventTime, setNewEventTime] = React.useState("09:00")
  const [currentTime, setCurrentTime] = React.useState("")

  const [events, setEvents] = React.useState<CalendarEvent[]>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("bold_calendar_events")
        if (saved) return JSON.parse(saved)
      } catch {}
    }
    return [
      { id: "1", dateStr: new Date().toISOString().slice(0, 10), title: "Reunião de Alinhamento", time: "10:00" },
      { id: "2", dateStr: new Date().toISOString().slice(0, 10), title: "Revisão do Projeto", time: "15:30" },
    ]
  })

  // Sincroniza data com props externas se fornecidas
  React.useEffect(() => {
    if (propDate) {
      setSelectedDate(propDate)
      setViewDate(propDate)
    }
  }, [propDate])

  // Relógio ao vivo
  React.useEffect(() => {
    const updateTime = () => {
      const now = new Date()
      setCurrentTime(
        now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
      )
    }
    updateTime()
    const interval = setInterval(updateTime, 1000)
    return () => clearInterval(interval)
  }, [])

  const saveEvents = (updated: CalendarEvent[]) => {
    setEvents(updated)
    try {
      localStorage.setItem("bold_calendar_events", JSON.stringify(updated))
    } catch {}
  }

  const handleSelectDate = (d: Date) => {
    setSelectedDate(d)
    if (propSetDate) propSetDate(d)
  }

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))
  }

  const handleGoToToday = () => {
    const today = new Date()
    setViewDate(today)
    handleSelectDate(today)
  }

  const handleAddEvent = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newEventTitle.trim()) return

    const dateKey = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
    const newEv: CalendarEvent = {
      id: Date.now().toString(),
      dateStr: dateKey,
      title: newEventTitle.trim(),
      time: newEventTime,
      completed: false,
    }

    saveEvents([...events, newEv])
    setNewEventTitle("")
  }

  const handleToggleEvent = (id: string) => {
    saveEvents(
      events.map((ev) => (ev.id === id ? { ...ev, completed: !ev.completed } : ev))
    )
  }

  const handleDeleteEvent = (id: string) => {
    saveEvents(events.filter((ev) => ev.id !== id))
  }

  // Gera dias da grade do mês
  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()

  const firstDayOfMonth = new Date(year, month, 1)
  const lastDayOfMonth = new Date(year, month + 1, 0)

  const startingDayIndex = firstDayOfMonth.getDay() // 0 = Domingo
  const daysInMonth = lastDayOfMonth.getDate()

  const prevMonthLastDay = new Date(year, month, 0).getDate()

  const selectedDateKey = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
  const selectedHolidayKey = `${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
  const selectedDayHoliday = BRAZILIAN_HOLIDAYS[selectedHolidayKey]

  const selectedDayEvents = events.filter((ev) => ev.dateStr === selectedDateKey)

  const isToday = (d: number, m: number, y: number) => {
    const today = new Date()
    return today.getDate() === d && today.getMonth() === m && today.getFullYear() === y
  }

  const isSelected = (d: number, m: number, y: number) => {
    return (
      selectedDate.getDate() === d &&
      selectedDate.getMonth() === m &&
      selectedDate.getFullYear() === y
    )
  }

  // Se renderizado como Widget compacto
  if (isWidget) {
    return (
      <div className="flex flex-col h-full bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 p-3 font-sans select-none justify-between">
        <div className="flex items-center justify-between pb-2 border-b border-zinc-100 dark:border-zinc-800">
          <div>
            <span className="text-xs font-bold text-zinc-900 dark:text-white capitalize">
              {MONTH_NAMES[month]} {year}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400 cursor-pointer"
            >
              <ChevronLeft className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={handleGoToToday}
              className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-[#004EB1]/10 dark:bg-blue-950 text-[#004EB1] dark:text-blue-400 cursor-pointer"
            >
              Hoje
            </button>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400 cursor-pointer"
            >
              <ChevronRight className="size-3.5" />
            </button>
          </div>
        </div>

        {/* Grade compacta */}
        <div className="grid grid-cols-7 gap-1 text-center mt-2">
          {WEEK_DAYS.map((wd) => (
            <span key={wd} className="text-[9px] font-bold text-zinc-400 dark:text-zinc-500">
              {wd[0]}
            </span>
          ))}

          {/* Dias do mês anterior */}
          {Array.from({ length: startingDayIndex }).map((_, i) => {
            const dayNum = prevMonthLastDay - startingDayIndex + i + 1
            return (
              <span key={`prev-${i}`} className="text-[10px] text-zinc-300 dark:text-zinc-700 py-1">
                {dayNum}
              </span>
            )
          })}

          {/* Dias do mês atual */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const dayNum = i + 1
            const today = isToday(dayNum, month, year)
            const sel = isSelected(dayNum, month, year)
            const dKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`
            const hasEvents = events.some((ev) => ev.dateStr === dKey)

            return (
              <button
                key={dayNum}
                type="button"
                onClick={() => handleSelectDate(new Date(year, month, dayNum))}
                className={`relative text-[11px] font-medium py-1 rounded-lg transition-all cursor-pointer ${
                  sel
                    ? "bg-[#004EB1] text-white font-bold shadow-xs"
                    : today
                    ? "bg-blue-50 dark:bg-blue-950/40 text-[#004EB1] dark:text-blue-400 font-bold"
                    : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-750 dark:text-zinc-300"
                }`}
              >
                {dayNum}
                {hasEvents && !sel && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 size-1 rounded-full bg-blue-500" />
                )}
              </button>
            )
          })}
        </div>

        {/* Rodapé rápido do dia selecionado */}
        <div className="pt-2 border-t border-zinc-100 dark:border-zinc-800 mt-2 flex items-center justify-between text-[10px] text-zinc-500 dark:text-zinc-400">
          <span className="truncate">
            {selectedDate.getDate()} {MONTH_NAMES[selectedDate.getMonth()]} • {WEEK_DAYS[selectedDate.getDay()]}
          </span>
          <span className="font-bold text-[#004EB1] dark:text-blue-400 shrink-0">
            {selectedDayEvents.length} compromisso(s)
          </span>
        </div>
      </div>
    )
  }

  // Visão completa do aplicativo (App Viewport)
  return (
    <div className="flex-1 w-full h-full overflow-hidden bg-zinc-50 dark:bg-zinc-950 p-4 sm:p-6 lg:p-8 flex items-center justify-center font-sans">
      <div className="max-w-4xl w-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row h-full max-h-[640px]">
        {/* Painel Esquerdo: Data Atual, Relógio & Agenda do Dia */}
        <div className="w-full md:w-80 bg-zinc-50/80 dark:bg-zinc-950/60 border-b md:border-b-0 md:border-r border-zinc-200/80 dark:border-zinc-800/80 p-6 flex flex-col justify-between shrink-0">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="size-8 rounded-xl bg-blue-500/10 dark:bg-blue-500/20 text-[#004EB1] dark:text-blue-400 flex items-center justify-center">
                  <CalendarDays className="size-4" />
                </div>
                <div>
                  <h3 className="font-bold text-xs text-zinc-900 dark:text-white uppercase tracking-wider">
                    Calendário
                  </h3>
                  <p className="text-[10px] text-zinc-400">Bold Workspace</p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-[11px] font-mono text-zinc-500 dark:text-zinc-400 bg-white dark:bg-zinc-900 px-2.5 py-1 rounded-lg border border-zinc-200/50 dark:border-zinc-800">
                <Clock className="size-3 text-[#004EB1] dark:text-blue-400" />
                <span>{currentTime || "--:--"}</span>
              </div>
            </div>

            {/* Cartão de Destaque da Data Selecionada */}
            <div className="p-4 rounded-2xl bg-gradient-to-br from-[#004EB1] to-[#0066d6] text-white shadow-lg space-y-1">
              <div className="flex items-baseline justify-between">
                <span className="text-4xl font-extrabold tracking-tight">
                  {String(selectedDate.getDate()).padStart(2, "0")}
                </span>
                <span className="text-xs font-bold uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-md">
                  {WEEK_DAYS_FULL[selectedDate.getDay()]}
                </span>
              </div>
              <p className="text-xs font-medium text-blue-100">
                {MONTH_NAMES[selectedDate.getMonth()]} de {selectedDate.getFullYear()}
              </p>
              {selectedDayHoliday && (
                <div className="pt-2 mt-2 border-t border-white/20 flex items-center gap-1.5 text-[11px] font-semibold text-amber-200">
                  <Sparkles className="size-3.5" />
                  <span>Feriado: {selectedDayHoliday}</span>
                </div>
              )}
            </div>
          </div>

          {/* Agenda & Lembretes do Dia */}
          <div className="space-y-3 mt-4 flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold text-zinc-800 dark:text-zinc-200 uppercase tracking-wider flex items-center gap-1.5">
                <Bell className="size-3 text-[#004EB1] dark:text-blue-400" />
                <span>Agenda ({selectedDayEvents.length})</span>
              </h4>
            </div>

            <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 min-h-[100px] max-h-[160px]">
              {selectedDayEvents.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-4 text-zinc-400 dark:text-zinc-600 text-xs">
                  Nenhum compromisso marcado para este dia.
                </div>
              ) : (
                selectedDayEvents.map((ev) => (
                  <div
                    key={ev.id}
                    className={`flex items-center justify-between p-2 rounded-xl border transition-all text-xs ${
                      ev.completed
                        ? "bg-zinc-100/50 dark:bg-zinc-900/40 border-zinc-200/40 text-zinc-400 line-through"
                        : "bg-white dark:bg-zinc-900 border-zinc-200/80 dark:border-zinc-800 text-zinc-800 dark:text-zinc-200 shadow-2xs"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleToggleEvent(ev.id)}
                      className="flex items-center gap-2 min-w-0 text-left cursor-pointer flex-1"
                    >
                      <CheckCircle2
                        className={`size-3.5 shrink-0 ${
                          ev.completed ? "text-emerald-500" : "text-zinc-300 dark:text-zinc-600"
                        }`}
                      />
                      <div className="min-w-0 truncate">
                        <p className="font-semibold truncate">{ev.title}</p>
                        {ev.time && <span className="text-[10px] text-zinc-400">{ev.time}</span>}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteEvent(ev.id)}
                      className="text-zinc-350 hover:text-red-500 p-1 rounded transition-colors cursor-pointer"
                    >
                      <Trash2 className="size-3" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Adicionar Rápido */}
            <form onSubmit={handleAddEvent} className="flex gap-1.5 pt-2 border-t border-zinc-200/60 dark:border-zinc-800/60">
              <input
                type="text"
                value={newEventTitle}
                onChange={(e) => setNewEventTitle(e.target.value)}
                placeholder="Novo compromisso..."
                className="flex-1 bg-white dark:bg-zinc-900 border border-zinc-250 dark:border-zinc-800 px-2.5 py-1.5 rounded-xl text-xs text-zinc-800 dark:text-zinc-200 focus:outline-none focus:ring-1 focus:ring-[#004EB1]"
              />
              <button
                type="submit"
                className="size-8 rounded-xl bg-[#004EB1] hover:bg-[#003d8d] text-white flex items-center justify-center cursor-pointer transition-colors shadow-xs shrink-0"
              >
                <Plus className="size-4" />
              </button>
            </form>
          </div>
        </div>

        {/* Painel Direito: Grade do Mês Completa */}
        <div className="flex-1 p-6 flex flex-col justify-between overflow-y-auto">
          {/* Navegação do Mês */}
          <div className="flex items-center justify-between pb-4 border-b border-zinc-100 dark:border-zinc-800">
            <div>
              <h2 className="text-xl font-bold text-zinc-900 dark:text-white capitalize tracking-tight">
                {MONTH_NAMES[month]} <span className="text-zinc-400 dark:text-zinc-500 font-normal">{year}</span>
              </h2>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="p-2 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300 cursor-pointer border border-zinc-200/60 dark:border-zinc-800 transition-colors"
                title="Mês anterior"
              >
                <ChevronLeft className="size-4" />
              </button>
              <button
                type="button"
                onClick={handleGoToToday}
                className="px-3 py-1.5 text-xs font-bold rounded-xl bg-zinc-100 dark:bg-zinc-800 hover:bg-[#004EB1] hover:text-white dark:hover:bg-[#004EB1] text-zinc-800 dark:text-zinc-200 cursor-pointer transition-all border border-zinc-200/60 dark:border-zinc-800"
              >
                Hoje
              </button>
              <button
                type="button"
                onClick={handleNextMonth}
                className="p-2 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-300 cursor-pointer border border-zinc-200/60 dark:border-zinc-800 transition-colors"
                title="Próximo mês"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
          </div>

          {/* Grade do Calendário */}
          <div className="mt-4 flex-1 flex flex-col">
            {/* Cabeçalho dos dias da semana */}
            <div className="grid grid-cols-7 gap-2 mb-2 text-center">
              {WEEK_DAYS.map((wd) => (
                <span key={wd} className="text-xs font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 py-1">
                  {wd}
                </span>
              ))}
            </div>

            {/* Dias do mês */}
            <div className="grid grid-cols-7 gap-2 flex-1 items-stretch">
              {/* Dias do mês anterior */}
              {Array.from({ length: startingDayIndex }).map((_, i) => {
                const dayNum = prevMonthLastDay - startingDayIndex + i + 1
                return (
                  <div
                    key={`prev-${i}`}
                    className="flex flex-col items-center justify-center p-2 rounded-2xl text-zinc-300 dark:text-zinc-700 text-xs select-none opacity-40"
                  >
                    {dayNum}
                  </div>
                )
              })}

              {/* Dias do mês atual */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const dayNum = i + 1
                const today = isToday(dayNum, month, year)
                const sel = isSelected(dayNum, month, year)
                const dKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`
                const hKey = `${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`
                const isHoliday = !!BRAZILIAN_HOLIDAYS[hKey]
                const dayEvents = events.filter((ev) => ev.dateStr === dKey)

                return (
                  <button
                    key={dayNum}
                    type="button"
                    onClick={() => handleSelectDate(new Date(year, month, dayNum))}
                    className={`group relative flex flex-col items-center justify-between p-2 rounded-2xl transition-all cursor-pointer border ${
                      sel
                        ? "bg-[#004EB1] text-white font-bold border-[#004EB1] shadow-lg scale-105 z-10"
                        : today
                        ? "bg-blue-50/70 dark:bg-blue-950/30 border-blue-400/50 text-[#004EB1] dark:text-blue-400 font-bold"
                        : isHoliday
                        ? "bg-amber-50/50 dark:bg-amber-950/20 border-amber-300/40 text-amber-700 dark:text-amber-300 hover:bg-amber-100/60"
                        : "bg-white dark:bg-zinc-900 border-zinc-150/80 dark:border-zinc-800/80 text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100/70 dark:hover:bg-zinc-800/60"
                    }`}
                  >
                    <span className="text-sm">{dayNum}</span>

                    {/* Indicadores de Feriado ou Eventos */}
                    <div className="flex items-center gap-1 mt-1 min-h-[6px]">
                      {isHoliday && (
                        <span
                          title={BRAZILIAN_HOLIDAYS[hKey]}
                          className={`size-1.5 rounded-full ${sel ? "bg-amber-300" : "bg-amber-500"}`}
                        />
                      )}
                      {dayEvents.length > 0 && (
                        <span
                          title={`${dayEvents.length} compromisso(s)`}
                          className={`size-1.5 rounded-full ${sel ? "bg-white" : "bg-blue-500"}`}
                        />
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function DesktopCalendarWidget({
  calendarDate: propDate,
  setCalendarDate: propSetDate,
  dimension = "medium",
}: {
  calendarDate?: Date
  setCalendarDate?: (date: Date | undefined) => void
  dimension?: "medium" | "large"
}) {
  const [selectedDate, setSelectedDate] = React.useState<Date>(() => propDate || new Date())
  const [viewDate, setViewDate] = React.useState<Date>(() => propDate || new Date())

  React.useEffect(() => {
    if (propDate) {
      setSelectedDate(propDate)
      setViewDate(propDate)
    }
  }, [propDate])

  const handleSelectDate = (d: Date) => {
    setSelectedDate(d)
    if (propSetDate) propSetDate(d)
  }

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))
  }

  const handleGoToToday = () => {
    const today = new Date()
    setViewDate(today)
    handleSelectDate(today)
  }

  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()

  const firstDayOfMonth = new Date(year, month, 1)
  const lastDayOfMonth = new Date(year, month + 1, 0)
  const startingDayIndex = firstDayOfMonth.getDay()
  const daysInMonth = lastDayOfMonth.getDate()
  const prevMonthLastDay = new Date(year, month, 0).getDate()

  const isToday = (d: number, m: number, y: number) => {
    const today = new Date()
    return today.getDate() === d && today.getMonth() === m && today.getFullYear() === y
  }

  const isSelected = (d: number, m: number, y: number) => {
    return (
      selectedDate.getDate() === d &&
      selectedDate.getMonth() === m &&
      selectedDate.getFullYear() === y
    )
  }

  if (dimension === "large") {
    const selectedHolidayKey = `${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
    const holiday = BRAZILIAN_HOLIDAYS[selectedHolidayKey]

    return (
      <div className="flex h-full gap-3 text-white font-sans select-none overflow-hidden p-0.5">
        <div className="w-1/3 flex flex-col justify-between bg-white/5 rounded-xl p-2.5 border border-white/10 shrink-0">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-300 block truncate">
              {WEEK_DAYS_FULL[selectedDate.getDay()]}
            </span>
            <div className="text-3xl font-extrabold tracking-tight mt-0.5">
              {String(selectedDate.getDate()).padStart(2, "0")}
            </div>
            <p className="text-[11px] text-white/70 font-medium truncate">
              {MONTH_NAMES[selectedDate.getMonth()]} {selectedDate.getFullYear()}
            </p>
          </div>
          {holiday ? (
            <div className="text-[10px] text-amber-300 font-semibold bg-amber-500/10 p-1.5 rounded-lg border border-amber-500/20 truncate">
              ★ {holiday}
            </div>
          ) : (
            <div className="text-[10px] text-white/50">Dia útil</div>
          )}
        </div>

        <div className="flex-1 flex flex-col justify-between h-full min-w-0">
          <div className="flex items-center justify-between pb-1">
            <span className="text-xs font-bold text-white capitalize truncate">
              {MONTH_NAMES[month]} {year}
            </span>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="p-1 rounded hover:bg-white/10 text-white/80 cursor-pointer"
              >
                <ChevronLeft className="size-3" />
              </button>
              <button
                type="button"
                onClick={handleGoToToday}
                className="px-1.5 py-0.5 text-[8px] font-bold rounded bg-[#004EB1] text-white cursor-pointer"
              >
                Hoje
              </button>
              <button
                type="button"
                onClick={handleNextMonth}
                className="p-1 rounded hover:bg-white/10 text-white/80 cursor-pointer"
              >
                <ChevronRight className="size-3" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {WEEK_DAYS.map((wd) => (
              <span key={wd} className="text-[9px] font-bold text-white/50">
                {wd[0]}
              </span>
            ))}
            {Array.from({ length: startingDayIndex }).map((_, i) => {
              const dayNum = prevMonthLastDay - startingDayIndex + i + 1
              return (
                <span key={`prev-${i}`} className="text-[10px] text-white/20 py-0.5">
                  {dayNum}
                </span>
              )
            })}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const dayNum = i + 1
              const today = isToday(dayNum, month, year)
              const sel = isSelected(dayNum, month, year)
              return (
                <button
                  key={dayNum}
                  type="button"
                  onClick={() => handleSelectDate(new Date(year, month, dayNum))}
                  className={`text-[11px] font-medium py-0.5 rounded-md transition-all cursor-pointer ${
                    sel
                      ? "bg-[#004EB1] text-white font-bold shadow-xs"
                      : today
                      ? "bg-blue-500/20 text-blue-300 font-bold border border-blue-400/40"
                      : "hover:bg-white/10 text-white/90"
                  }`}
                >
                  {dayNum}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  // 2x2 Desktop Widget compacto
  return (
    <div className="flex flex-col h-full w-full justify-between text-white font-sans select-none overflow-hidden p-0.5">
      {/* Navegação Topo */}
      <div className="flex items-center justify-between pb-1 shrink-0">
        <span className="text-xs font-bold text-white capitalize tracking-tight truncate">
          {MONTH_NAMES[month]} {year}
        </span>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={handlePrevMonth}
            className="p-1 rounded-md hover:bg-white/10 text-white/80 transition-colors cursor-pointer"
            title="Mês anterior"
          >
            <ChevronLeft className="size-3" />
          </button>
          <button
            type="button"
            onClick={handleGoToToday}
            className="px-1.5 py-0.5 text-[8px] font-bold rounded-md bg-[#004EB1] text-white transition-all cursor-pointer hover:bg-blue-600"
          >
            Hoje
          </button>
          <button
            type="button"
            onClick={handleNextMonth}
            className="p-1 rounded-md hover:bg-white/10 text-white/80 transition-colors cursor-pointer"
            title="Próximo mês"
          >
            <ChevronRight className="size-3" />
          </button>
        </div>
      </div>

      {/* Grade dos dias da semana */}
      <div className="grid grid-cols-7 gap-1 text-center shrink-0 mb-0.5">
        {WEEK_DAYS.map((wd) => (
          <span key={wd} className="text-[9px] font-extrabold text-white/50 uppercase">
            {wd[0]}
          </span>
        ))}
      </div>

      {/* Grade de dias com dimensionamento adaptável ao espaço 2x2 */}
      <div className="grid grid-cols-7 gap-1 text-center flex-1 items-stretch">
        {/* Dias do mês anterior */}
        {Array.from({ length: startingDayIndex }).map((_, i) => {
          const dayNum = prevMonthLastDay - startingDayIndex + i + 1
          return (
            <div
              key={`prev-${i}`}
              className="flex items-center justify-center text-[9px] text-white/25 select-none"
            >
              {dayNum}
            </div>
          )
        })}

        {/* Dias do mês atual */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dayNum = i + 1
          const today = isToday(dayNum, month, year)
          const sel = isSelected(dayNum, month, year)
          const hKey = `${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`
          const isHoliday = !!BRAZILIAN_HOLIDAYS[hKey]

          return (
            <button
              key={dayNum}
              type="button"
              onClick={() => handleSelectDate(new Date(year, month, dayNum))}
              className={`relative flex items-center justify-center text-[11px] font-medium rounded-md transition-all cursor-pointer ${
                sel
                  ? "bg-[#004EB1] text-white font-bold shadow-xs scale-105"
                  : today
                  ? "bg-blue-500/20 text-blue-300 font-bold border border-blue-400/40"
                  : isHoliday
                  ? "text-amber-300 font-semibold hover:bg-white/10"
                  : "hover:bg-white/10 text-white/90"
              }`}
            >
              <span>{dayNum}</span>
              {isHoliday && !sel && (
                <span className="absolute bottom-0.5 size-0.5 rounded-full bg-amber-400" />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

